// Libraries
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router';
import styled, { ThemeProvider } from 'styled-components';
// eslint-disable-next-line import/no-absolute-path, import/no-unresolved
import '/node_modules/flag-icons/css/flag-icons.min.css';

// Dependencies
import Routes from 'containers/Routes';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/hy-am';
import 'dayjs/locale/it';
import 'dayjs/locale/km';
import 'dayjs/locale/lo';
import 'dayjs/locale/lv';
import 'dayjs/locale/pt';
import 'dayjs/locale/sk';
import 'dayjs/locale/th';
import 'dayjs/locale/vi';
import isoWeek from 'dayjs/plugin/isoWeek';
import withErrorHandler from 'hocs/withErrorHandler';
import { TranslationsProvider } from 'providers/translationsProvider';
import { getAntTheme } from 'styles/antTheme';
import 'styles/chatbox.css';
import 'styles/index.css';
import { mainTheme } from 'styles/themes';
import { initBeforeUnload } from 'utils/beforeUnload';
import getTranslationHandlers, { languages } from 'utils/translationHandlers';

// Actions and selectors
import { selectors as langSelectors } from 'store/modules/translations';
import { actions as authActions, selectors as userSelectors } from 'store/modules/user';

// TODO: This file should in in styles, not here.
import GlobalStyle from './globalStyle';

const WrapperComponent = styled.div`
  min-height: 100vh;
`;

const getPageTitle = () => {
  if (window.prod) {
    return 'Bridge for Billions';
  }

  if (window.staging) {
    return 'Bridge for Billions - Demo';
  }

  return 'Dev - Bridge for Billions';
};

dayjs.extend(isoWeek);

const setLanguage = lang => {
  switch (lang) {
    case 'es_es':
      dayjs.locale('es');
      break;
    case 'fr_fr':
      dayjs.locale('fr');
      break;
    case 'pt_br':
      dayjs.locale('pt-br');
      break;
    case 'pt_pt':
      dayjs.locale('pt');
      break;
    case 'hy_hy':
      dayjs.locale('hy-am');
      break;
    case 'th_th':
      dayjs.locale('th');
      break;
    case 'vi_vi':
      dayjs.locale('vi');
      break;
    case 'it_it':
      dayjs.locale('it');
      break;
    case 'sk_sk':
      dayjs.locale('sk');
      break;
    case 'lv_lv':
      dayjs.locale('lv');
      break;
    case 'km_km':
      dayjs.locale('km');
      break;
    case 'lo_lo':
      dayjs.locale('lo');
      break;
    default:
      dayjs.locale('en-gb');
  }
};

const App = () => {
  const dispatch = useDispatch();

  const selectedLanguage = useSelector(langSelectors.getSelectedLanguage);
  const userIsAccessible = useSelector(userSelectors.userIsAccessible);

  const configProviderTheme = getAntTheme(selectedLanguage);
  setLanguage(selectedLanguage);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const joinList = urlParams.get('join-list');
    if (joinList) {
      sessionStorage.setItem('joinList', joinList);
    }
  }, []);

  useEffect(() => {
    dispatch(authActions.userRestore());
    initBeforeUnload();
  }, []);

  const translationHandlers = getTranslationHandlers(selectedLanguage);

  const pageTitle = getPageTitle();

  return (
    <ThemeProvider theme={mainTheme}>
      <ConfigProvider theme={configProviderTheme}>
        <TranslationsProvider value={translationHandlers}>
          <GlobalStyle language={selectedLanguage} />
          <WrapperComponent>
            <Helmet>
              <meta charSet="utf-8" />
              <title>{pageTitle}</title>

              {/* Google font Phetsarath for LAO language */}
              {selectedLanguage === languages.LO && (
                <link
                  href="https://fonts.googleapis.com/css2?family=Phetsarath:wght@400;700&display=swap"
                  rel="stylesheet"
                />
              )}
              {/* Google font Hanuman for Khmer */}
              {selectedLanguage === languages.KM && (
                <link
                  href="https://fonts.googleapis.com/css2?family=Hanuman:wght@100;300;400;700;900&display=swap"
                  rel="stylesheet"
                />
              )}
            </Helmet>
            {userIsAccessible && (
              <BrowserRouter>
                <Routes />
              </BrowserRouter>
            )}
          </WrapperComponent>
        </TranslationsProvider>
      </ConfigProvider>
    </ThemeProvider>
  );
};

const WrappedApp = withErrorHandler(App);
export default WrappedApp;
